/**
 * Escolha de um pagamento PENDENTE — a entrada do passo 2 do ato de pagar.
 *
 * Antes do desmembramento, quem dava baixa numa fatura DIGITAVA o pagamento ali mesmo: valor,
 * data, meio, pagador. Agora o dinheiro já entrou (passo 1, financeiro) e o que falta é dizer o
 * que ele paga. Então a entrada não é um formulário, é uma ESCOLHA — e o que se escolhe é uma
 * linha de dinheiro que existe.
 *
 * Por isso a lista é só de PENDENTES: um pagamento já sinalizado tem a conta fechada por outra
 * pessoa, e oferecê-lo levaria o usuário a uma recusa do servidor depois de todo o trabalho de
 * preencher o resto.
 *
 * Compartilhado entre o modal da tela de Títulos (uma fatura) e o passo 1 da Identificação de
 * Pagamento (um lote): os dois escolhem a MESMA coisa, e duas listas divergiriam na primeira
 * mudança de coluna.
 */
import { useCallback, useEffect, useState } from 'react';
import { FaInfoCircle, FaSyncAlt } from 'react-icons/fa';

import { PaymentRow, listPaymentsByStatus } from '../../pages/billing/services/billingApi';
import { formatBRL } from '../../../utils/currencyUtils';
import { formatTaxId } from '../../../utils/taxIdUtils';

/** Rótulos dos meios e tipos, como nas tabelas de cobrança. */
const METHOD_LABEL: Record<string, string> = {
    PIX: 'PIX', TED: 'TED', TRANSFER: 'Transferência', BOLETO: 'Boleto', ESCROW: 'Conta Escrow',
};

const TYPE_WITHOUT_METHOD: Record<string, string> = {
    DEBT_CONFESSION: 'Confissão de dívida',
    ASSET_DATION: 'Dação em pagamento',
};

/**
 * O que aparece na coluna Meio.
 *
 * `paymentMethod` é NULO fora de pagamento financeiro, e isso não é falta de dado: confissão de
 * dívida e dação não têm meio. Mostrar "—" ali esconderia a natureza do lançamento justamente nos
 * dois casos em que ela mais muda a conversa.
 */
export const paymentMethodLabel = (p: PaymentRow): string =>
    METHOD_LABEL[p.paymentMethod || ''] || TYPE_WITHOUT_METHOD[p.paymentType] || '—';

/** Instante em UTC → leitura local. É o dia em que o dinheiro entrou, do ponto de vista de quem olha. */
export const localDate = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

/**
 * O pagamento crava o valor procurado?
 *
 * Compara em CENTAVOS arredondados: `0.1 + 0.2 !== 0.3` em ponto flutuante, e um pagamento de
 * 1.234,56 comparado com 1.234,56 pode dar diferente por um resto binário — o destaque não
 * apareceria justamente na linha certa.
 */
export const matchesExactAmount = (row: PaymentRow, target?: number): boolean => {
    if (target == null || !target) return false;
    return Math.round((row.amount || 0) * 100) === Math.round(target * 100);
};

const TH = 'px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300';
const TD = 'px-3 py-2 text-sm';

interface Props {
    /** pagamento já escolhido (a linha fica destacada) */
    selectedId?: string | null;
    onSelect: (payment: PaymentRow) => void;
    /** abre o modal de informações daquele pagamento */
    onInfo?: (payment: PaymentRow) => void;
    /**
     * Valor que fecha a conta exatamente (o da fatura, no modal de uma fatura só).
     *
     * As linhas que cravam esse valor entram DESTACADAS. Não é enfeite: numa lista de pagamentos
     * pendentes de dias diferentes, achar a olho qual bate com a fatura é o trabalho chato — e é
     * um trabalho que a tela sabe fazer. Os outros valores continuam escolhíveis: pagar menos é
     * desconto e pagar mais é acréscimo, os dois legítimos.
     */
    exactAmount?: number;
}

const PendingPaymentPicker: React.FC<Props> = ({ selectedId, onSelect, onInfo, exactAmount }) => {
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setRows(await listPaymentsByStatus('PENDING'));
        } catch (e: any) {
            setError(e?.message || 'Falha ao carregar os pagamentos pendentes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <p className="text-sm text-gray-500 py-4 text-center">Carregando pagamentos...</p>;

    if (error) {
        return (
            <div className="py-4 text-center">
                <p className="text-sm text-red-600">{error}</p>
                <button onClick={load} className="mt-2 text-sm text-blue-600 hover:underline">
                    Tentar de novo
                </button>
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="py-6 text-center">
                {/* dizer o CAMINHO, não só que a lista está vazia: quem marca faturas não é quem
                    registra o dinheiro, e sem isso a tela parece quebrada */}
                <p className="text-sm text-gray-500">Nenhum pagamento pendente de identificação.</p>
                <p className="text-xs text-gray-400 mt-1">
                    O dinheiro é registrado pelo financeiro na tela de <strong>Pagamentos</strong>.
                    Sem um pagamento registrado, não há o que marcar.
                </p>
                <button onClick={load}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
                    <FaSyncAlt /> Atualizar
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500">
                    {rows.length === 1 ? '1 pagamento pendente' : `${rows.length} pagamentos pendentes`}
                </p>
                <button onClick={load} title="Atualizar a lista"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
                    <FaSyncAlt /> Atualizar
                </button>
            </div>

            <div className="border dark:border-slate-600 rounded overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                            <tr>
                                <th className={TH}>Data</th>
                                <th className={`${TH} text-right`}>Valor</th>
                                <th className={TH}>Meio</th>
                                <th className={TH}>Pagador</th>
                                <th className={TH} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const isSelected = row.paymentId === selectedId;
                                const matchesInvoiceAmount = matchesExactAmount(row, exactAmount);
                                return (
                                    <tr key={row.paymentId}
                                        /* Quem crava o valor da fatura vai em ITÁLICO, não com
                                           legenda embaixo do valor: a legenda repetia em texto o
                                           que a linha já mostra (o número é igual ao da fatura) e
                                           empurrava as outras colunas para baixo.

                                           A escolha do usuário vence o destaque: depois de
                                           selecionar, ele precisa ver ONDE clicou, não o que a
                                           tela sugeria antes. */
                                        className={`border-t dark:border-slate-600 ${matchesInvoiceAmount ? 'italic' : ''} ${isSelected
                                            ? 'bg-blue-50 dark:bg-blue-900/30'
                                            : matchesInvoiceAmount
                                                ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                : 'hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                                        <td className={TD} title={matchesInvoiceAmount
                                            ? 'Este pagamento crava o valor da fatura'
                                            : undefined}>{localDate(row.paymentDate)}</td>
                                        <td className={`${TD} text-right font-semibold`}>
                                            {formatBRL(row.amount || 0)}
                                        </td>
                                        <td className={TD}>{paymentMethodLabel(row)}</td>
                                        <td className={TD}>
                                            {row.payer?.name || '—'}
                                            {row.payer?.documentNumber && (
                                                <span className="block text-xs text-gray-500">
                                                    {formatTaxId(row.payer.documentNumber)}
                                                </span>
                                            )}
                                        </td>
                                        <td className={`${TD} text-right whitespace-nowrap`}>
                                            {onInfo && (
                                                <button onClick={() => onInfo(row)}
                                                    aria-label={`Informações do pagamento de ${formatBRL(row.amount || 0)}`}
                                                    title="Mais informações deste pagamento"
                                                    className="text-gray-400 hover:text-blue-600 mr-2 align-middle">
                                                    <FaInfoCircle />
                                                </button>
                                            )}
                                            <button onClick={() => onSelect(row)}
                                                className={`px-3 py-1 text-xs rounded ${isSelected
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 dark:bg-slate-600 hover:bg-gray-300'}`}>
                                                {isSelected ? 'Selecionado' : 'Selecionar'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PendingPaymentPicker;
